import { lazy, Suspense, type ComponentType } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import { AppShell } from "./components/AppShell";
import { Brand } from "./components/Brand";
import { PageLoader } from "./components/States";

function lazyNamed<TModule extends Record<string, ComponentType>>(
  loader: () => Promise<TModule>,
  exportName: keyof TModule & string,
) {
  return lazy(async () => {
    const module = await loader();
    const Component = module[exportName];
    if (!Component) {
      throw new Error(`Missing export "${exportName}" from lazy module`);
    }
    return { default: Component };
  });
}

const LoginPage = lazyNamed(() => import("./pages/LoginPage"), "LoginPage");
const SettingsPage = lazyNamed(() => import("./pages/SettingsPage"), "SettingsPage");
const MembersPage = lazyNamed(() => import("./pages/MembersPage"), "MembersPage");
const AcceptInvitePage = lazyNamed(() => import("./pages/AcceptInvitePage"), "AcceptInvitePage");
const ApplyPage = lazyNamed(() => import("./pages/ApplyPage"), "ApplyPage");
const DisclosuresPage = lazyNamed(() => import("./pages/DisclosuresPage"), "DisclosuresPage");
const DisclosureDetailPage = lazyNamed(
  () => import("./pages/DisclosureDetailPage"),
  "DisclosureDetailPage",
);

function SessionLoader() {
  return (
    <output className="grid min-h-screen place-items-center bg-ink text-white">
      <div className="text-center">
        <Brand />
        <p className="mt-6 text-sm text-neutral-400">Checking your session</p>
      </div>
    </output>
  );
}

function ProtectedLayout() {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return <SessionLoader />;
  }
  if (!user) {
    return (
      <Navigate to="/login" state={{ from: `${location.pathname}${location.search}` }} replace />
    );
  }
  return <AppShell />;
}

function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/apply" element={<ApplyPage />} />
        <Route path="/accept-invite" element={<AcceptInvitePage />} />
        <Route element={<ProtectedLayout />}>
          <Route index element={<Navigate to="/disclosures" replace />} />
          <Route path="conditions" element={<Navigate to="/disclosures" replace />} />
          <Route path="conditions/*" element={<Navigate to="/disclosures" replace />} />
          <Route path="payroll" element={<Navigate to="/disclosures" replace />} />
          <Route path="payroll/*" element={<Navigate to="/disclosures" replace />} />
          <Route path="projects" element={<Navigate to="/disclosures" replace />} />
          <Route path="projects/*" element={<Navigate to="/disclosures" replace />} />
          <Route path="disclosures" element={<DisclosuresPage />} />
          <Route path="disclosures/:id" element={<DisclosureDetailPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="members" element={<MembersPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

export function App() {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
}
